<template>
  <div class="user-audit">
    <!-- <h2 class="text-xl font-semibold mb-4">用户审核</h2> -->
    
    <!-- 搜索和筛选 -->
    <el-card class="mb-4">
      <el-form :inline="true" :model="searchForm" class="search-form">
        <el-form-item label="搜索" class="search-item">
          <el-input
            v-model="searchForm.keyword"
            placeholder="用户名/邮箱/手机号/单位名称"
            clearable
            @clear="handleSearch"
          >
            <template #append>
              <el-button @click="handleSearch">
                <el-icon><Search /></el-icon>
              </el-button>
            </template>
          </el-input>
        </el-form-item>
        <el-form-item label="状态" class="search-item">
          <el-select v-model="searchForm.status" placeholder="审核状态" clearable @change="handleSearch">
            <el-option label="待审核" value="pending" />
            <el-option label="已通过" value="approved" />
            <el-option label="已拒绝" value="rejected" />
          </el-select>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 用户列表 -->
    <el-card class="table-card">
      <template #header>
        <div class="card-header">
          <span class="text-lg font-medium">待审核用户列表</span>
        </div>
      </template>

      <div class="table-container">
        <el-table
          v-loading="loading"
          :data="userList"
          style="width: 100%"
          border
          stripe
          size="small"
        >
          <el-table-column prop="id" label="ID" width="50" fixed="left" />
          <el-table-column prop="name" label="用户名" min-width="80" fixed="left" />
          <el-table-column prop="profession" label="单位类型" min-width="80" show-overflow-tooltip />
          <el-table-column prop="institution_name" label="单位名称" min-width="120" show-overflow-tooltip />
          <el-table-column prop="organization" label="单位" min-width="80" show-overflow-tooltip />
          <el-table-column prop="purpose" label="使用目的" min-width="120" show-overflow-tooltip />
          <el-table-column prop="phone" label="电话" min-width="100" show-overflow-tooltip />
          <el-table-column prop="wechat_name" label="微信号" min-width="100" show-overflow-tooltip />
          <el-table-column prop="email" label="邮箱" min-width="120" show-overflow-tooltip />
          <el-table-column prop="created_at" label="申请时间" min-width="120" show-overflow-tooltip />
          <el-table-column prop="status" label="状态" width="70" fixed="right">
            <template #default="{ row }">
              <el-tag :type="getStatusType(row.status)" size="small">
                {{ getStatusText(row.status) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="180" fixed="right">
            <template #default="{ row }">
              <div class="action-buttons">
                <el-button
                  v-if="row.status === 'pending'"
                  type="success"
                  size="small"
                  @click="handleApprove(row)"
                >
                  通过
                </el-button>
                <el-button
                  v-if="row.status === 'pending'"
                  type="danger"
                  size="small"
                  @click="handleReject(row)"
                >
                  拒绝
                </el-button>
                <el-button
                  v-if="row.status === 'rejected' || row.status === 'approved'"
                  type="primary"
                  size="small"
                  @click="handleReaudit(row)"
                >
                  重新审核
                </el-button>
              </div>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <!-- 分页 -->
      <div class="pagination-container">
        <el-pagination
          :current-page="currentPage"
          :page-size="pageSize"
          :page-sizes="[10, 20, 50, 100]"
          :total="total"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </el-card>

    <!-- 审核确认对话框 -->
    <el-dialog
      v-model="auditDialogVisible"
      :title="auditType === 'approve' ? '确认通过' : '确认拒绝'"
      width="400px"
      class="audit-dialog"
    >
      <div class="text-center">
        <el-icon :size="48" :class="auditType === 'approve' ? 'text-success' : 'text-danger'">
          <component :is="auditType === 'approve' ? 'CircleCheck' : 'CircleClose'" />
        </el-icon>
        <p class="mt-4 text-lg">
          确认{{ auditType === 'approve' ? '通过' : '拒绝' }}用户 "{{ selectedUser && selectedUser.name }}" 的申请吗？
        </p>
      </div>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="auditDialogVisible = false">取消</el-button>
          <el-button
            :type="auditType === 'approve' ? 'success' : 'danger'"
            @click="confirmAudit"
          >
            确认
          </el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Search, CircleCheck, CircleClose } from '@element-plus/icons-vue'
import axios from 'axios'

// 搜索表单
const searchForm = reactive({
  keyword: '',
  status: ''
})

// 表格数据
const loading = ref(false)
const userList = ref([])
const currentPage = ref(1)
const pageSize = ref(10)
const total = ref(0)

// 审核对话框
const auditDialogVisible = ref(false)
const auditType = ref('approve')
const selectedUser = ref(null)

// 获取状态类型
const getStatusType = (status) => {
  const types = {
    pending: 'warning',
    approved: 'success',
    rejected: 'danger'
  }
  return types[status] || 'info'
}

// 获取状态文本
const getStatusText = (status) => {
  const texts = {
    pending: '待审核',
    approved: '已通过',
    rejected: '已拒绝'
  }
  return texts[status] || status
}

// 获取用户列表
const fetchUserList = async () => {
  loading.value = true
  try {
    const params = {
      page: currentPage.value,
      limit: pageSize.value,
      status: searchForm.status || undefined,
      keyword: searchForm.keyword || undefined
    }
    
    const response = await axios.get('/api/admin/users', { params })
    userList.value = response.data.data
    total.value = response.data.total
  } catch (error) {
    ElMessage.error('获取用户列表失败：' + error.message)
  } finally {
    loading.value = false
  }
}

// 处理搜索
const handleSearch = () => {
  currentPage.value = 1
  fetchUserList()
}

// 处理分页
const handleSizeChange = (val) => {
  pageSize.value = val
  fetchUserList()
}

const handleCurrentChange = (val) => {
  currentPage.value = val
  fetchUserList()
}

// 处理审核
const handleApprove = (row) => {
  selectedUser.value = row
  auditType.value = 'approve'
  auditDialogVisible.value = true
}

const handleReject = (row) => {
  selectedUser.value = row
  auditType.value = 'reject'
  auditDialogVisible.value = true
}

// 处理重新审核
const handleReaudit = async (row) => {
  try {
    await ElMessageBox.confirm(
      `确认重新审核用户 "${row.name}" 的申请吗？`,
      '重新审核',
      {
        confirmButtonText: '确认',
        cancelButtonText: '取消',
        type: 'warning'
      }
    )
    
    await axios.put(`/api/admin/users/${row.id}/reaudit`)
    ElMessage.success('已重置审核状态')
    fetchUserList()
  } catch (error) {
    if (error !== 'cancel') {
      ElMessage.error('操作失败：' + error.message)
    }
  }
}

// 确认审核
const confirmAudit = async () => {
  if (!selectedUser.value) return
  
  try {
    const action = auditType.value === 'approve' ? 'approve' : 'reject'
    await axios.put(`/api/admin/users/${selectedUser.value.id}/${action}`)
    ElMessage.success(`用户已${action === 'approve' ? '通过' : '拒绝'}`)
    auditDialogVisible.value = false
    fetchUserList()
  } catch (error) {
    ElMessage.error('操作失败：' + error.message)
  }
}

onMounted(() => {
  fetchUserList()
})
</script>

<style scoped>
.user-audit {
  padding: 0.75rem;
}

@media (min-width: 768px) {
  .user-audit {
    padding: 1rem;
  }
}

.search-form {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: flex-start;
}

.search-item {
  margin-bottom: 0;
  min-width: 160px;
}

.search-item :deep(.el-select) {
  width: 100%;
}

.search-item :deep(.el-input) {
  width: 100%;
}

.card-header {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.75rem;
}

@media (min-width: 768px) {
  .card-header {
    flex-direction: row;
    align-items: center;
  }
}

.table-container {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.action-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.pagination-container {
  margin-top: 0.75rem;
  display: flex;
  justify-content: flex-end;
  overflow-x: auto;
}

/* 分页响应式优化 */
@media screen and (max-width: 768px) {
  .pagination-container {
    justify-content: center;
  }
  
  :deep(.el-pagination) {
    flex-wrap: wrap;
    gap: 0.5rem;
  }
  
  :deep(.el-pagination .el-pager) {
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem;
  }
}

:deep(.el-card__header) {
  padding: 0.75rem;
}

:deep(.el-table) {
  margin-top: 0.75rem;
  font-size: 0.8125rem;
}

:deep(.el-pagination) {
  margin-top: 0.75rem;
  font-size: 0.8125rem;
}

:deep(.el-form-item__label) {
  font-size: 0.8125rem;
  line-height: 28px;
  width: 80px !important;
  text-align: right;
}

:deep(.el-input__inner) {
  font-size: 0.8125rem;
  height: 28px;
  line-height: 28px;
}

:deep(.el-button) {
  font-size: 0.8125rem;
  padding: 5px 10px;
}

:deep(.el-dialog__title) {
  font-size: 0.9375rem;
}

:deep(.el-dialog__body) {
  font-size: 0.8125rem;
}

:deep(.el-tag) {
  font-size: 0.75rem;
  padding: 0 8px;
  height: 22px;
  line-height: 20px;
}

/* 响应式搜索表单 */
@media screen and (max-width: 1200px) {
  .search-item {
    min-width: 140px;
  }
}

@media screen and (max-width: 992px) {
  .search-item {
    min-width: 120px;
  }
}

/* 响应式布局调整 */
@media screen and (max-width: 768px) {
  .search-form {
    flex-direction: column;
  }

  .search-item {
    width: 100%;
    min-width: auto;
  }

  .el-form-item {
    width: 100%;
    margin-bottom: 0.75rem;
  }

  .el-input {
    width: 100%;
  }

  .el-select {
    width: 100%;
  }

  .action-buttons {
    flex-direction: column;
    gap: 0.25rem;
  }

  .action-buttons .el-button {
    width: 100%;
    font-size: 0.75rem;
    padding: 2px 4px;
  }
}

/* 表格在小屏幕上的优化 */
@media screen and (max-width: 640px) {
  :deep(.el-table) {
    font-size: 0.75rem;
  }

  :deep(.el-table .cell) {
    padding-left: 0.25rem;
    padding-right: 0.25rem;
  }

  :deep(.el-table__body-wrapper) {
    overflow-x: auto;
  }

  :deep(.el-table__header-wrapper) {
    overflow-x: auto;
  }

  :deep(.el-pagination) {
    flex-wrap: wrap;
    justify-content: center;
    font-size: 0.75rem;
  }

  :deep(.el-table-column--fixed-left) {
    z-index: 2;
  }

  :deep(.el-table-column--fixed-right) {
    z-index: 2;
  }
}

@media screen and (max-width: 480px) {
  :deep(.el-table) {
    font-size: 0.7rem;
  }

  :deep(.el-table .cell) {
    padding-left: 0.2rem;
    padding-right: 0.2rem;
  }

  :deep(.el-tag) {
    font-size: 0.65rem;
    padding: 0 4px;
    height: 18px;
    line-height: 16px;
  }

  .action-buttons .el-button {
    font-size: 0.7rem;
    padding: 1px 3px;
  }
}

/* 对话框响应式调整 */
@media screen and (max-width: 480px) {
  .audit-dialog {
    width: 90%;
    max-width: 320px;
  }
}

/* 确保表格在小屏幕上可以水平滚动 */
@media screen and (max-width: 1200px) {
  .table-container {
    overflow-x: auto;
  }
  
  :deep(.el-table) {
    min-width: 1000px;
  }
}

@media screen and (max-width: 768px) {
  :deep(.el-table) {
    min-width: 800px;
  }
}

/* 超小屏幕优化 */
@media screen and (max-width: 375px) {
  .user-audit {
    padding: 0.5rem;
  }
  
  :deep(.el-card) {
    margin-bottom: 0.5rem;
  }
  
  :deep(.el-card__header) {
    padding: 0.5rem;
  }
  
  :deep(.el-card__body) {
    padding: 0.5rem;
  }
  
  :deep(.el-table) {
    font-size: 0.65rem;
  }
  
  :deep(.el-table .cell) {
    padding-left: 0.15rem;
    padding-right: 0.15rem;
  }
  
  :deep(.el-tag) {
    font-size: 0.6rem;
    padding: 0 3px;
    height: 16px;
    line-height: 14px;
  }
  
  .action-buttons .el-button {
    font-size: 0.65rem;
    padding: 1px 2px;
  }
  
  :deep(.el-pagination) {
    font-size: 0.7rem;
  }
  
  :deep(.el-pagination .btn-prev),
  :deep(.el-pagination .btn-next) {
    padding: 0 0.5rem;
  }
}
</style>
